import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  deleteProduct,
  fetchAdminProducts,
  getProductStock,
} from '../lib/adminApi';
import { formatPrice } from '../../utils/formatPrice';

function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdminProducts();
      setProducts(data);
    } catch (err) {
      setError(err.message || 'Could not load products.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(products.map(p => p.category).filter(Boolean));
    return Array.from(set).sort();
  }, [products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter(product => {
      if (statusFilter !== 'all' && (product.status || 'published') !== statusFilter) {
        return false;
      }
      if (categoryFilter !== 'all' && product.category !== categoryFilter) {
        return false;
      }
      if (!q) return true;
      return (
        product.name?.toLowerCase().includes(q) ||
        product.brand?.toLowerCase().includes(q) ||
        product.category?.toLowerCase().includes(q) ||
        product.sku?.toLowerCase().includes(q)
      );
    });
  }, [products, query, statusFilter, categoryFilter]);

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteProduct(product.id);
      setProducts(current => current.filter(p => p.id !== product.id));
    } catch (err) {
      setError(err.message || 'Could not delete product.');
    }
  };

  const thumb = (product) => {
    const images = [...(product.product_images || [])].sort((a, b) => a.sort_order - b.sort_order);
    const main = images.find(i => i.is_thumbnail) || images[0];
    return main?.image_url || product.image_url || '';
  };

  return (
    <div>
      <div className="admin-topbar" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="admin-page-title">Products</h1>
          <p className="admin-page-subtitle">Manage catalog, inventory, and visibility</p>
        </div>
        <Link to="/admin/products/new" className="admin-btn admin-btn-primary">
          Add Product
        </Link>
      </div>

      <div className="admin-card">
        <div className="admin-toolbar">
          <input
            className="admin-search"
            placeholder="Search products..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <select
            className="admin-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="hidden">Hidden</option>
            <option value="out_of_stock">Out of stock</option>
            <option value="archived">Archived</option>
          </select>
          <select
            className="admin-select"
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {error && <p className="admin-error">{error}</p>}
        {loading && <p className="admin-empty">Loading products...</p>}

        {!loading && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(product => {
                  const stock = getProductStock(product);
                  const image = thumb(product);
                  return (
                    <tr key={product.id}>
                      <td>
                        {image ? (
                          <img src={image} alt="" className="admin-thumb" />
                        ) : (
                          <div className="admin-thumb" />
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{product.name}</div>
                        {product.brand && (
                          <div style={{ color: '#777', fontSize: 12 }}>{product.brand}</div>
                        )}
                      </td>
                      <td>{product.category}</td>
                      <td>
                        {product.sale_price != null ? (
                          <>
                            <span style={{ fontWeight: 600 }}>{formatPrice(product.sale_price)}</span>
                            <span style={{ color: '#999', textDecoration: 'line-through', marginLeft: 6 }}>
                              {formatPrice(product.price)}
                            </span>
                          </>
                        ) : (
                          formatPrice(product.price)
                        )}
                      </td>
                      <td>{stock.available}</td>
                      <td>
                        <span className={`admin-badge ${product.status || 'published'}`}>
                          {product.status || 'published'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Link
                            to={`/admin/products/${product.id}`}
                            className="admin-btn admin-btn-secondary admin-btn-sm"
                          >
                            Edit
                          </Link>
                          <button
                            type="button"
                            className="admin-btn admin-btn-danger admin-btn-sm"
                            onClick={() => handleDelete(product)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!filtered.length && (
              <p className="admin-empty">No products match your filters.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Products;
